import { generateUUID } from '../../utilities/uuid.utils';
import { toast } from 'react-toastify';
import { getTaskManagementEscalation } from '../../services/Triggers/TaskIssuesTriggers';

export interface AutocompleteOption {
  label: string;
  value: string;
}

// Constants (preserved exactly with optimized typing)
export const communicationChannels = ['EMAIL', 'SMS', 'CALL', 'WHATSAPP'] as const;

export const filterOptions = [
  'All',
  'Open',
  'High Priority Open',
  'Medium Priority Open',
  'Low Priority Open',
  'Closed',
  'High Priority Closed',
  'Medium Priority Closed',
  'Low Priority Closed',
] as const;

export const limitOptions = ['5', '10', '15', '25', '50'] as const;
export const templateOptions = ['Template', 'Text'] as const;
export const reportFormats = ['pdf', 'summary'] as const;
export const communicationOptions = ['EMAIL', 'SMS', 'CALL', 'WHATSAPP'] as const;

export const communicationMapping: Record<string, string> = {
  EMAIL: 'Email Communication',
  SMS: 'SMS Communication',
  CALL: 'Phone Communication',
  WHATSAPP: 'Whatsapp Communication',
};

// Type guards and interfaces
interface TriggerConditions {
  observationFreq?: number;
  observationSensorTag?: string;
  observationSensorId?: string;
  resolutionFreq?: number;
  resolutionSensorTag?: string;
  resolutionSensorId?: string;
  resolutionValue?: number;
  resolutionUnit?: string;
}

interface TriggerData {
  _id?: string;
  type: string;
  scope: string;
  name?: string;
  description?: string;
  userGroup?: any;
  workspace?: any;
  assets?: any;
  conditions?: TriggerConditions;
  recurrenceText?: string;
  startDate?: number | Date;
  recurrence?: any;
  totalOccurrence?: number;
}

export const isTriggerFormComplete = (triggerData: TriggerData): boolean => {
  const requiredFieldsMap = new Map<string, string[]>([
    ['conditional', ['name', 'description', 'scope', 'type', 'userGroup', 'workspace', 'assets']],
    ['One Time', ['name', 'description', 'scope', 'type', 'startDate']],
    ['Recurring', ['name', 'description', 'scope', 'type', 'startDate', 'recurrence']],
  ]);

  const fields = requiredFieldsMap.get(triggerData.type) || [];
  return fields.every(field => {
    const value = triggerData[field as keyof TriggerData];
    return !!value && (typeof value !== 'string' || value.trim() !== '');
  });
};

export const getTimeUnits = () => ['Minute(s)', 'Hour(s)', 'Day(s)', 'Week(s)'] as const;

export const actionLabels = [
  'Create Sub Task',
  'Send Whatsapp Message',
  'Send Email',
  'Send SMS',
  'Assign to a different user',
  'Call',
] as const;

// Validation helpers
export const validateTrigger = async (triggerData: any) => {
  let requiredFields: string[] = [];
  let updatedTriggerData: any = {};
  const missingFields: string[] = [];

  // Helper for userGroup: if triggerData._id exists and userGroup is an object, return its _id
  const getUserGroupValue = () => {
    if (triggerData.userGroup) {
      return triggerData._id && typeof triggerData.userGroup === 'object'
        ? triggerData.userGroup._id
        : triggerData.userGroup;
    }
    return undefined;
  };

  // Helper for workspace: if triggerData._id exists and workspace is an object, return its _id
  const getWorkspaceValue = () => {
    if (triggerData.workspace) {
      return triggerData._id && typeof triggerData.workspace === 'object'
        ? triggerData.workspace._id
        : triggerData.workspace;
    }
    return undefined;
  };

  // Helper for assets: if triggerData._id exists and assets is an array of objects, map to their _id; otherwise return as-is.
  const getAssetsValue = () => {
    if (triggerData.assets) {
      if (
        triggerData._id &&
        Array.isArray(triggerData.assets) &&
        triggerData.assets.length > 0 &&
        typeof triggerData.assets[0] === 'object'
      ) {
        return triggerData.assets.map((asset: any) => asset._id);
      }
      return triggerData.assets;
    }
    return undefined;
  };

  switch (triggerData.type) {
    case 'conditional':
      requiredFields = [
        'name',
        'description',
        'type',
        'scope',
        ...(triggerData.scope === 'Group' ? ['userGroup'] : []),
        'workspace',
        'assets',
        'conditions',
      ];
      if (!triggerData.conditions) {
        missingFields.push('conditions');
      } else {
        const {
          observationFreq,
          observationSensorTag,
          observationSensorId,
          resolutionFreq,
          resolutionSensorTag,
          resolutionSensorId,
          resolutionValue,
          resolutionUnit,
        } = triggerData.conditions;
        if (!observationFreq) missingFields.push('observationFreq');
        if (!observationSensorTag) missingFields.push('observationSensorTag');
        if (!observationSensorId) missingFields.push('observationSensorId');
        if (resolutionFreq && resolutionFreq > 0) {
          if (!resolutionSensorTag) missingFields.push('resolutionSensorTag');
          if (!resolutionSensorId) missingFields.push('resolutionSensorId');
          if (!resolutionFreq) missingFields.push('resolutionFreq');
        } else {
          if (!resolutionValue) missingFields.push('resolutionValue');
          if (!resolutionUnit) missingFields.push('resolutionUnit');
        }
      }
      updatedTriggerData = {
        ...(triggerData._id ? { _id: triggerData._id } : {}),
        name: triggerData.name,
        description: triggerData.description,
        type: triggerData.type,
        scope: triggerData.scope,
        userGroup: getUserGroupValue(),
        workspace: getWorkspaceValue(),
        assets: getAssetsValue(),
        conditions: triggerData.conditions && {
          observationFreq: triggerData.conditions.observationFreq,
          observationSensorTag: triggerData.conditions.observationSensorTag,
          observationSensorId: triggerData.conditions.observationSensorId,
          ...(triggerData.conditions.resolutionFreq && triggerData.conditions.resolutionFreq > 0
            ? {
              resolutionFreq: triggerData.conditions.resolutionFreq,
              resolutionSensorTag: triggerData.conditions.resolutionSensorTag,
              resolutionSensorId: triggerData.conditions.resolutionSensorId,
            }
            : {
              resolutionValue: triggerData.conditions.resolutionValue,
              resolutionUnit: triggerData.conditions.resolutionUnit,
            }),
        },
      };
      break;
    case 'recurring':
      requiredFields = [
        'name',
        'description',
        'type',
        'scope',
        ...(triggerData.scope === 'Group' ? ['userGroup'] : []),
        'recurrenceText',
        'startDate',
        'recurrence',
      ];
      updatedTriggerData = {
        ...(triggerData._id ? { _id: triggerData._id } : {}),
        name: triggerData.name,
        description: triggerData.description,
        type: triggerData.type,
        scope: triggerData.scope,
        userGroup: getUserGroupValue(),
        recurrenceText: triggerData.recurrenceText,
        startDate:
          typeof triggerData.startDate === 'number'
            ? triggerData.startDate
            : new Date(triggerData.startDate).getTime(),
        recurrence: triggerData.recurrence,
        ...(triggerData?.totalOccurrence ? { totalOccurrence: triggerData?.totalOccurrence } : {}),
      };
      break;
    case 'onetime':
      requiredFields = [
        'name',
        'description',
        'type',
        'scope',
        ...(triggerData.scope === 'Group' ? ['userGroup'] : []),
        'startDate',
      ];
      updatedTriggerData = {
        ...(triggerData._id ? { _id: triggerData._id } : {}),
        name: triggerData.name,
        description: triggerData.description,
        type: triggerData.type,
        scope: triggerData.scope,
        userGroup: getUserGroupValue(),
        startDate:
          typeof triggerData.startDate === 'number'
            ? triggerData.startDate
            : new Date(triggerData.startDate).getTime(),
      };
      break;
    default:
      toast.error('Invalid trigger type.');
      return;
  }

  requiredFields.forEach(field => {
    if (!triggerData[field]) missingFields.push(field);
  });

  if (missingFields.length > 0) {
    toast.error(`Please fill in: ${missingFields[0]}`);
    return;
  }

  return updatedTriggerData;
};

export const TRIGGER_TYPES = [
  { label: 'One Time', value: 'onetime' },
  { label: 'Recurring', value: 'recurring' },
  { label: 'Conditional', value: 'conditional' },
];
export const SCOPE_OPTIONS = [
  { label: 'System', value: 'System' },
  { label: 'User Group', value: 'Group' },
];

const SYSTEM_SCOPE = 'System';

export interface ScopeChoice {
  readonly canSearchPeople: boolean;
  readonly currentScope: string;
}

export const scopeOptionsFor = <Option extends { readonly value: string }>(
  options: readonly Option[],
  { canSearchPeople, currentScope }: ScopeChoice,
): readonly Option[] =>
  options.filter((option) => option.value !== SYSTEM_SCOPE || canSearchPeople || currentScope === SYSTEM_SCOPE);
export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const getCurrentDay = () => DAYS_OF_WEEK[new Date().getDay()];
export const getCurrentDateFormatted = () =>
  new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

export const mapRecurrenceToRule = (option: string): object | null => {
  const currentDate = new Date();
  const daysOfWeek = [currentDate.getDay()]; // Always an array
  const dayOfMonth = [currentDate.getDate()]; // Always an array
  const month = [currentDate.getMonth() + 1];
  switch (option) {
    case 'Daily':
      return { frequency: 'DAILY' };
    case `Weekly on ${DAYS_OF_WEEK[currentDate.getDay()]}`:
      return { frequency: 'WEEKLY', daysOfWeek };
    case `Monthly on the First ${DAYS_OF_WEEK[currentDate.getDay()]}`:
      return { frequency: 'MONTHLY', weekOfMonth: [1], daysOfWeek };
    case `Annually on ${getCurrentDateFormatted()}`:
      return { frequency: 'YEARLY', dayOfMonth, month };
    case 'Every weekday (Monday to Friday)':
      return { frequency: 'WEEKLY', daysOfWeek: [0, 1, 2, 3, 4] };
    case 'Custom':
      return { custom: true };
    default:
      return null;
  }
};

export const initialTaskState: any = {
  TaskFormDataId: generateUUID(),
  taskData: null,
  assigneeMethod: null,
  assetId: '',
  assignee: null,
  taskExpectedTime: '',
  taskExpectedUnit: '',
  taskDeadlineTime: '',
  taskDeadlineUnit: '',
  taskCompletion: '',
  attachmentId: '',
  priority: '',
  taskType: '',
  isObsolete: false,
  taskCompleted: false,
  richTextContent: '',
  dataEntry: false,
  complexity: '',
  equipmentSelected: '',
  taskCompletionState: '',
  skillsSelected: [],
  sensorId: null,
  dataInputTag: '',
  escalations: [{ unit: '', value: 0, userIds: [], communicationMedium: '' }],
  componentActions: [],
};

export const capitalizeFirstLetter = (input: string): string =>
  input ? input[0].toUpperCase() + input.slice(1).toLowerCase() : '';

export function cleanEscalations(task: any) {
  if (Array.isArray(task.escalations)) {
    const validEscalations = task.escalations.filter(
      (esc: any) =>
        esc.value &&
        esc.unit &&
        esc.userIds &&
        Array.isArray(esc.userIds) &&
        esc.userIds.length > 0 &&
        esc.communicationMedium
    );
    task.escalations = validEscalations.length > 0 ? validEscalations : [];
  } else {
    task.escalations = [];
  }
  return task;
}

function prefillEscalationsRecursively(tasks: any[], escalations: any[]) {
  return tasks.map(task => {
    const shouldUpdateEscalations =
      !task.escalations ||
      task.escalations.length === 0 ||
      (task.escalations.length === 1 &&
        task.escalations[0].value === 0 &&
        task.escalations[0].unit === '' &&
        task.escalations[0].userIds.length === 0 &&
        task.escalations[0].communicationMedium === '');

    const updatedTask = {
      ...task,
      escalations: shouldUpdateEscalations ? escalations : task.escalations,
    };

    if (Array.isArray(task.subTasks) && task.subTasks.length > 0) {
      updatedTask.subTasks = prefillEscalationsRecursively(task.subTasks, escalations);
    }

    return updatedTask;
  });
}

export async function fetchEscalationData({
  props,
  escalationDataFetched,
  setFormData,
  setEscalationDataFetched,
}: {
  props: any,
  escalationDataFetched: boolean,
  setFormData: (fn: (prev: any) => any) => void,
  setEscalationDataFetched: (v: boolean) => void,
}) {
  const isEditMode = !!props.taskComponentDetails && props.taskComponentDetails.length > 0;
  if (!isEditMode && props.open && props.triggerData?._id && !escalationDataFetched) {
    try {
      const escalationData = await getTaskManagementEscalation(props.triggerData._id);
      if (escalationData && escalationData.length > 0 && escalationData[0]?.escalations?.length > 0) {
        const transformedEscalations = escalationData[0]?.escalations?.map((esc: any) => ({
          value: esc.value || 0,
          unit: esc.unit || '',
          userIds: esc.userIds || [],
          communicationMedium: 'EMAIL',
        })) || [];
        setFormData((prev: any) => ({
          tasks: prefillEscalationsRecursively(prev.tasks, transformedEscalations),
        }));
      }
      setEscalationDataFetched(true);
    } catch (error) {
      setEscalationDataFetched(true);
    }
  }
}

export { prefillEscalationsRecursively };
